import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";
import BadgePopup from "../components/Badgepopup";

function IslamicGuide70() {
  const [isOpen, setIsOpen] = useState(true);
  const contentRef = useRef(null);
  const titleText = "Why Islamic Banking is NOT a solution";
  const links = {
    prev: "/islamicguide-69",
    index: "/index-islamicguide",
    next: "/islamicguide-70",
  };

  const questions = [
    {
      text: "1. According to the lesson, what is the main criticism of modern Islamic banking practices?",
      options: [
        {
          value: "A",
          label: "A. They completely prohibit all forms of trade and profit",
        },
        {
          value: "B",
          label:
            "B. They focus too much on charitable activities instead of finance",
        },
        {
          value: "C",
          label:
            "C. They often replicate conventional interest-based lending using Shariah-compliant terminology",
        },
        {
          value: "D",
          label: "D. They rely entirely on cryptocurrency for transactions",
        },
      ],
      correct: "C",
    },
    {
      text: "2. Why are contracts like Murabaha or Tawarruq criticized in the context described?",
      options: [
        {
          value: "A",
          label: "A. Because they are explicitly forbidden in the Qur’an",
        },
        {
          value: "B",
          label:
            "B. Because they are frequently used without genuine trade intent, serving as disguised loans",
        },
        { value: "C", label: "C. Because they involve excessive risk-sharing" },
        { value: "D", label: "D. Because they eliminate all forms of profit" },
      ],
      correct: "B",
    },
    {
      text: "3. What societal harm does easy access to credit create according to the lesson?",
      options: [
        { value: "A", label: "A. It increases long-term savings rates" },
        {
          value: "B",
          label:
            "B. It encourages financial discipline and careful consumption",
        },
        {
          value: "C",
          label:
            "C. It promotes consumerism, debt dependence, and short-term thinking",
        },
        { value: "D", label: "D. It eliminates economic inequality entirely" },
      ],
      correct: "C",
    },
    {
      text: "4. Why is Bitcoin presented as a closer alignment with Islamic financial principles?",
      options: [
        { value: "A", label: "A. Because it guarantees profits without risk" },
        {
          value: "B",
          label:
            "B. Because it is based on scarcity, proof-of-work, and discourages debt-driven consumption",
        },
        {
          value: "C",
          label: "C. Because it replaces all forms of trade with lending",
        },
        { value: "D", label: "D. Because it allows unlimited money creation" },
      ],
      correct: "B",
    },
  ];

  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);

  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={70}
        timerMinutes={60}
        currentLevel={5}
        unlockedBadges={9}
      />
      <p className="content" ref={contentRef}>
        Welcome to <b>Day 70</b>! Today’s lesson critically examines the modern
        practice of Islamic banking and questions whether it truly fulfills the
        spirit and objectives of Shariah. We explore how many so-called
        Shariah-compliant financial products mirror conventional interest-based
        systems in form and outcome, and why this disconnect has broader moral,
        social, and economic consequences. Against this backdrop, the lesson
        introduces Bitcoin as a radically different monetary paradigm that
        emphasizes scarcity, transparency, real effort, and financial
        discipline.
        <br />
        <br />
        <b>The Problem with Islamic Banking Structures</b>
        <br />
        <br />
        Islam is a religion that emphasizes justice, fairness, and transparency
        in all dealings, especially in financial transactions. At its heart, the
        prohibition of Riba is meant to prevent exploitation and to ensure that
        wealth creation is tied to productive activities rather than mere
        financial manipulation. Yet, in the modern era, we find that much of
        what is marketed under the label of “Islamic Banking” is nothing more
        than a restructured form of conventional interest-based lending. Instead
        of creating new and innovative models of productive investment, Islamic
        banks largely function by taking the same usurious mechanisms and
        repackaging them with new names. They often use contracts such as
        Murabaha, Ijara, or Tawarruq, not as authentic trade instruments, but as
        tools to mimic interest-bearing loans while giving them a veneer of
        permissibility.
        <br />
        At the core of these practices is an attempt to make an impermissible
        act appear permissible. A simple example is when a bank buys a commodity
        at a lower price and then sells it to a customer at a higher price on
        deferred payment terms. On the surface, this looks like a trade
        transaction. However, in reality, the buyer has no genuine need for the
        commodity, and the seller has no genuine intent to profit from the risks
        of holding or trading that commodity. Instead, both parties are engaged
        only in a disguised lending arrangement. The bank ensures a guaranteed
        profit, and the customer is simply receiving cash flow in exchange for
        paying a higher amount later, which is exactly what Riba achieves in
        conventional banking. This kind of transaction contradicts the Islamic
        principle that deeds are judged by intentions. A sale made with the sole
        purpose of making interest appear like a legitimate trade is not a true
        sale; it is a deception. Thus, Islamic banking often fails the test of
        sincerity, authenticity, and alignment with the maqasid (objectives) of
        Shariah.
        <br />
        <br />
        <b>The Consequences of Easy Credit in Society</b>
        <br />
        <br />
        One of the major harms of the Islamic banking sector, just like its
        conventional counterpart, is the way it makes credit easily available in
        society. When money is easy to borrow, financial discipline is eroded.
        People begin to purchase items far beyond their means, succumbing to the
        lure of consumerism. A family that might have lived within its modest
        resources suddenly finds itself indebted for luxuries that were once out
        of reach, such as large homes, extravagant weddings, or unnecessary
        consumer goods. When Islamic banks facilitate such loans under the guise
        of Shariah-compliant contracts, they inadvertently encourage
        extravagance, greed, and waste all of which have been condemned in
        Islam. The Quran clearly warns against israf (extravagance) and tabdhir
        (wastefulness), and yet this banking model feeds directly into those
        very behaviors.
        <br />
        Moreover, easy access to credit creates a culture of short-term
        thinking. Instead of planning for the future, saving diligently, or
        investing wisely, individuals and businesses resort to borrowing as the
        first solution. Poorly run businesses that would never survive on their
        own merits are artificially sustained through cheap loans, leading to
        inefficiencies and financial fragility in the economy. This process
        inflates asset prices, encourages speculative investment, and undermines
        true value creation. In contrast, when borrowing is difficult, people
        are forced to live within their means, build reputations for
        trustworthiness, and cultivate financial intelligence to earn the
        confidence of their peers or relatives for loans. Easy credit eliminates
        these virtues and replaces them with dependency and recklessness. In
        essence, Islamic banking, by adopting the same debt-based model as
        conventional banking, has failed to protect society from the very harms
        that the prohibition of Riba was meant to prevent.
        <br />
        <br />
        <b>Bitcoin as a Return to Financial Frugality</b>
        <br />
        <br />
        Against this backdrop, Bitcoin offers Muslims an entirely new paradigm.
        Unlike fiat money, which is created through debt and loses value over
        time, Bitcoin is built on scarcity, transparency, and genuine work. Its
        supply is capped at 21 million coins, ensuring that no central authority
        can inflate it at will. New Bitcoin is created only through
        proof-of-work, which requires real-world expenditure of energy and
        resources, anchoring it to tangible effort. This ensures that Bitcoin is
        not “money from nothing” but money earned through productive activity.
        For Muslims seeking a financial system free of Riba and deceit, Bitcoin
        represents a remarkable alternative that naturally aligns with Islamic
        values.
        <br />
        In a Bitcoin-based economy, savings become lucrative again. Because the
        supply is fixed and demand for Bitcoin is likely to rise, its purchasing
        power tends to increase over time. This encourages individuals to save
        rather than recklessly spend or borrow. It reinstills a culture of
        financial frugality where people are more careful in their consumption
        and investment choices. Instead of taking on easy loans for luxuries,
        individuals are motivated to preserve their wealth for genuine needs,
        productive ventures, and long-term security. For businesses, access to
        cheap credit is no longer guaranteed, so only projects that demonstrate
        real potential for productivity and profitability receive funding. This
        natural selection mechanism strengthens the overall economy, as weak and
        inefficient ventures are filtered out while strong and innovative ones
        thrive. In this way, Bitcoin not only provides a monetary network free
        from Riba but also revives the virtues of financial prudence, long-term
        thinking, and moderation in society.
        <br />
        <br />
        <b>A Future Beyond Islamic Banking</b>
        <br />
        <br />
        The rise of Bitcoin poses an important question for the Muslim world:
        should Islamic societies continue to cling to debt-based Islamic banking
        models that replicate the very evils of Riba, or should they embrace a
        truly transformative monetary system that aligns with the spirit of
        Shariah? The answer becomes clear when we analyze the outcomes. Islamic
        banking, despite its intentions, has built an entire sector based on
        facilitating cash flow for profit, rather than exploring productive
        investment avenues. It sustains consumerism, encourages dependence on
        credit, and undermines financial discipline. Bitcoin, on the other hand,
        empowers Muslims to create an economy where wealth is preserved, debt is
        discouraged, and investments are directed toward productive,
        value-generating activities.
        <br />
        It is important to clarify that adopting Bitcoin does not eliminate the
        concept of credit altogether. There will always be circumstances where
        borrowing is necessary and valid. However, under a Bitcoin standard,
        lenders are far less incentivized to lend purely for interest-based
        profit because their savings grow in value over time. This shifts the
        focus away from lending as a means of profit-making and towards genuine
        investment in ventures that produce goods, services, and societal
        benefit. Such a system is much closer to the Islamic vision of finance,
        where wealth circulates through trade, risk-sharing, and productive
        enterprise rather than exploitation. For the Muslim Ummah, embracing
        Bitcoin means moving beyond the superficial adjustments of Islamic
        banking and toward a truly just, transparent, and frugal financial
        future. It offers the possibility of freeing Muslim societies from the
        global debt trap, reviving the ethics of moderation, and aligning
        economic practices with the higher objectives of Islam.
        <br />
        <br />
        <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        This lesson highlights how much of contemporary Islamic banking relies
        on debt-based structures that imitate Riba through contractual formality
        rather than genuine trade or risk-sharing, leading to consumerism, easy
        credit, and weakened financial discipline. Such practices often
        undermine the maqasid of Shariah by encouraging extravagance,
        dependency, and speculative behavior. In contrast, Bitcoin is presented
        as an alternative that naturally discourages debt, rewards saving, and
        ties wealth creation to real work through proof-of-work and fixed
        supply. By promoting frugality, long-term thinking, and productive
        investment, Bitcoin offers Muslims a path beyond superficial “Islamic”
        financial engineering toward a more authentic, just, and Shariah-aligned
        economic system.
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)}
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>

      <FloatingPageNav
        prevLink={links.prev}
        indexLink={links.index}
        nextLink={links.next}
      />
      <BadgePopup
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        level="Level 5"
        badgeImg="./achieve.png"
        heading="Halal Hodler"
        subtitle="Graduate - Bitcoin Guide for Muslims"
        mainImg="./halalhodler.png"
        description="Honors your juhd to master the Bitcoin Protocol. You have achieved a deep understanding of how this asset's underlying structure guarantees absolute scarcity and is engineered to disallow unjust gain. You are now fully competent to analyze its compatibility with Islamic ethics for modern trade and wealth preservation."
        buttonText="Continue!"
      />
    </div>
  );
}

export default IslamicGuide70;
